import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import { FiArrowLeft, FiClock, FiBarChart2, FiExternalLink, FiCheckCircle, FiBookOpen, FiPlay } from "react-icons/fi";
import { TRAININGS } from "./data";

const translations = {
  en: {
    training_view: {
      back: "Back to modules",
      start_training: "Start Training",
      objectives: "Learning Objectives",
      about: "About this module",
      what_you_learn: "What you'll learn",
      duration: "Duration",
      level: "Level",
      category: "Category",
      not_found: "Training not found",
      go_back: "Go back to training list",
      opens_new_tab: "Opens in a new tab on 10000codeurs.com",
      ready_to_start: "Ready to start?",
      free_access: "Free access",
    },
  },
  fr: {
    training_view: {
      back: "Retour aux modules",
      start_training: "Commencer la formation",
      objectives: "Objectifs pédagogiques",
      about: "À propos",
      what_you_learn: "Au programme",
      duration: "Durée",
      level: "Niveau",
      category: "Catégorie",
      not_found: "Formation non trouvée",
      go_back: "Retour à la liste des formations",
      opens_new_tab: "S'ouvre sur 10000codeurs.com",
      ready_to_start: "Prêt à commencer ?",
      free_access: "Accès gratuit",
    },
  },
};

export default function TrainingView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useComponentTranslation(translations);

  const training = TRAININGS.find((tr) => tr.id === id);

  if (!training) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-6">
          <FiBookOpen className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t("training_view.not_found")}</h2>
        <Link to="/candidate/training" className="text-blue-600 hover:text-blue-700 font-medium">
          {t("training_view.go_back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      {/* Back button */}
      <button
        onClick={() => navigate("/candidate/training")}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-6 group"
      >
        <FiArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        {t("training_view.back")}
      </button>

      {/* Header Card */}
      <div className="relative bg-white rounded-3xl border border-gray-200 overflow-hidden mb-6 shadow-sm">
        {/* Gradient header */}
        <div 
          className="h-40 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${training.gradientFrom} 0%, ${training.gradientTo} 100%)` }}
        >
          {/* Pattern overlay */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          
          {/* Free badge */}
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full">
              {t("training_view.free_access")}
            </span>
          </div>
        </div>

        <div className="pt-8 px-8 pb-8">
          {/* Category & Title */}
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span 
              className="inline-block px-3 py-1 text-xs font-semibold rounded-full"
              style={{ backgroundColor: `${training.bgColor}15`, color: training.bgColor }}
            >
              {training.category}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{training.title}</h1>
          <p className="text-gray-600 text-lg leading-relaxed">{training.shortDescription}</p>

          {/* Meta badges */}
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <FiClock className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-400 font-medium">{t("training_view.duration")}</p>
                <p className="text-sm font-bold text-gray-900">{training.duration}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <FiBarChart2 className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-400 font-medium">{t("training_view.level")}</p>
                <p className="text-sm font-bold text-gray-900">{training.level}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* About */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FiBookOpen className="w-5 h-5" style={{ color: training.bgColor }} />
              {t("training_view.about")}
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              {training.description}
            </p>
          </div>

          {/* What you'll learn */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t("training_view.what_you_learn")}</h2>
            <ul className="space-y-3">
              {training.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${training.bgColor}15` }}
                  >
                    <FiCheckCircle className="w-4 h-4" style={{ color: training.bgColor }} />
                  </div>
                  <span className="text-gray-700 text-base">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Objectives */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t("training_view.objectives")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {training.objectives.map((objective, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                    style={{ backgroundColor: training.bgColor }}
                  >
                    {index + 1}
                  </div>
                  <span className="text-gray-700 text-sm leading-relaxed">{objective}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm sticky top-6">
            <p className="text-lg font-bold text-gray-900 mb-4">{t("training_view.ready_to_start")}</p>
            
            <a
              href={training.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 text-white font-semibold rounded-xl hover:opacity-90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${training.gradientFrom} 0%, ${training.gradientTo} 100%)` }}
            >
              <FiPlay className="w-5 h-5" />
              {t("training_view.start_training")}
              <FiExternalLink className="w-4 h-4 ml-1" />
            </a>
            
            <p className="text-xs text-gray-400 text-center mt-4">
              {t("training_view.opens_new_tab")}
            </p>

            {/* Additional info */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("training_view.duration")}</span>
                  <span className="font-semibold text-gray-900">{training.duration}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("training_view.level")}</span>
                  <span className="font-semibold text-gray-900">{training.level}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("training_view.category")}</span>
                  <span className="font-semibold text-gray-900">{training.category}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
